import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BoxService, BoxSizeService } from '@nebalkon-workspace/storage-api';
import type { BoxSizeResponse } from '@nebalkon-workspace/storage-api';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import {
  CabinetButtonComponent,
} from '@nebalkon-workspace/cabinet-client';
import { buildStaffQrLabelText } from '../utils/staff-qr-label-type.util';
import {
  NotInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';

@Component({
  selector: 'lib-staff-box-type-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, FormsModule, NotInput, CabinetButtonComponent],
  templateUrl: './staff-box-type-detail.component.html',
  styleUrl: './staff-box-type-detail.component.scss',
})
export class StaffBoxTypeDetailComponent implements OnInit {
  readonly #route = inject(ActivatedRoute);
  readonly #boxSizeService = inject(BoxSizeService);
  readonly #boxService = inject(BoxService);
  readonly #printer = inject(NiimbotPrinterService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);

  protected boxType = signal<BoxSizeResponse | null>(null);
  protected loading = signal(true);
  protected error = signal<string | null>(null);
  protected isToggling = signal(false);
  protected labelCopiesStr = signal('');
  protected isCreatingBox = signal(false);

  protected readonly printer = this.#printer;

  protected readonly canCreateAndPrint = computed(() => {
    this.labelCopiesStr();
    this.boxType();
    this.#printer.connectedDevice();
    const bt = this.boxType();
    if (!bt?.is_active) {
      return false;
    }
    if (!this.#printer.isConnected()) {
      return false;
    }
    return this.#parseLabelCopies() !== null;
  });

  public ngOnInit(): void {
    this.labelCopiesStr.set(String(this.#printer.getSavedLabelsPerBatch()));

    const boxTypeId = this.#route.snapshot.paramMap.get('id');
    if (boxTypeId) {
      this.#loadBoxTypeData(boxTypeId);
    } else {
      this.error.set(this.#translate.instant('admin.boxTypes.notFound'));
      this.loading.set(false);
    }
  }

  #loadBoxTypeData(id: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.#boxSizeService.getBoxSize(id).subscribe({
      next: (boxType) => {
        this.boxType.set(boxType);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading box type:', err);
        this.error.set(this.#translate.instant('admin.boxTypes.loadError'));
        this.loading.set(false);
      },
    });
  }

  protected async onToggleActive(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const newActiveState = target.checked;
    const boxType = this.boxType();

    if (!boxType || this.isToggling()) {
      target.checked = !newActiveState;
      return;
    }

    this.isToggling.set(true);

    try {
      if (newActiveState) {
        await firstValueFrom(this.#boxSizeService.activateBoxSize(boxType.id));
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.boxTypes.activateSuccess')],
        });
      } else {
        await firstValueFrom(this.#boxSizeService.deactivateBoxSize(boxType.id));
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.boxTypes.deactivateSuccess')],
        });
      }

      this.boxType.set({ ...boxType, is_active: newActiveState });
    } catch (toggleError) {
      console.error('Error toggling box type status:', toggleError);
      target.checked = !newActiveState;
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            newActiveState
              ? 'admin.boxTypes.activateError'
              : 'admin.boxTypes.deactivateError',
          ),
        ],
      });
    } finally {
      this.isToggling.set(false);
    }
  }

  protected formatVolumeM3(boxType: BoxSizeResponse): string {
    const m3 =
      (boxType.side1_mm / 1000) *
      (boxType.side2_mm / 1000) *
      (boxType.height_mm / 1000);
    return m3.toLocaleString('ru-RU', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  }

  protected onLabelCopiesChange(value: string | number | null): void {
    if (value === null || value === undefined) {
      this.labelCopiesStr.set('');
      return;
    }
    this.labelCopiesStr.set(String(value));
  }

  protected async onCreateAndPrint(): Promise<void> {
    const bt = this.boxType();
    const copies = this.#parseLabelCopies();
    if (!bt || copies === null || !this.canCreateAndPrint()) {
      return;
    }

    this.isCreatingBox.set(true);
    try {
      const box = await firstValueFrom(this.#boxService.createBoxByType(bt.id));
      const qr = box.id?.trim() ?? '';
      if (!qr) {
        throw new Error('NO_BOX_ID');
      }

      this.#printer.saveLabelsPerBatch(copies);

      try {
        await this.#printer.printQrCodeWithSavedLabelPreset(
          qr,
          copies,
          buildStaffQrLabelText(this.#translate, 'box'),
        );
      } catch (printErr) {
        console.error('Print error:', printErr);
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [this.#translate.instant('admin.boxTypes.printLabelsError')],
        });
        return;
      }

      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.boxTypes.createBoxSuccess')],
      });
    } catch (err) {
      console.error('Create box error:', err);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.boxTypes.createBoxError')],
      });
    } finally {
      this.isCreatingBox.set(false);
    }
  }

  #parseLabelCopies(): number | null {
    const raw = this.labelCopiesStr().trim();
    if (raw === '') {
      return null;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > 99) {
      return null;
    }
    if (String(n) !== raw.trim()) {
      return null;
    }
    return n;
  }
}
